import mixpanel from 'mixpanel-browser';
import type { Organization, UserProfile } from '../types/models';
import { backendApiClient } from './BackendApiClient';

// Thin wrapper so the rest of the app never imports mixpanel directly.
// Every call is a no-op when REACT_APP_MIXPANEL_TOKEN is empty (dev, staging, Jest).

export const AnalyticsEvents = {
  SignedUp: 'Signed Up',
  SignUpFailed: 'Sign Up Failed',
  SignUpStarted: 'Sign Up Started',
  SignUpCaptchaFailed: 'Sign Up Captcha Failed',
  EmailSuggestionShown: 'Email Suggestion Shown',
  EmailSuggestionAccepted: 'Email Suggestion Accepted',
  LoggedIn: 'Logged In',
  LoginFailed: 'Login Failed',
  LoggedOut: 'Logged Out',
  OrganizationSelected: 'Organization Selected',
  OrganizationCreated: 'Organization Created',
  InviteAccepted: 'Invite Accepted',
  PostCreated: 'Post Created',
  CommentAdded: 'Comment Added',
  ProposalCreated: 'Proposal Created',
  MeetingCreated: 'Meeting Created',
  MemberAdded: 'Member Added',
  DocumentUploaded: 'Document Uploaded',
  CheckoutStarted: 'Checkout Started',
  DemoStarted: 'Demo Started',
  DemoStepCompleted: 'Demo Step Completed',
  DemoFinished: 'Demo Finished',
} as const;

export type AnalyticsEvent = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

let enabled = false;

// The demo's fake user and org must not become the visitor's Mixpanel identity.
const canIdentify = () => enabled && !backendApiClient.isDemoMode();

export function initAnalytics(token = process.env.REACT_APP_MIXPANEL_TOKEN) {
  if (!token) return;
  mixpanel.init(token, {
    api_host: 'https://api-eu.mixpanel.com',
    autocapture: true,
    track_pageview: false,
    persistence: 'localStorage',
    record_sessions_percent: 100,
  });
  enabled = true;
}

export function trackPageView(path: string) {
  if (!enabled) return;
  mixpanel.track_pageview({ page: path });
}

// Keep props non-PII: ids, counts, types. No titles or free text.
export function track(event: AnalyticsEvent, props?: Record<string, string | number | boolean | null>) {
  if (!enabled) return;
  mixpanel.track(event, props);
}

export function identifyUser(user: UserProfile) {
  if (!canIdentify()) return;
  mixpanel.identify(user.id);
  mixpanel.people.set({ $email: user.email, $name: user.fullName });
}

export function setOrganization(org: Organization) {
  if (!canIdentify()) return;
  const props = {
    orgId: org.id,
    orgName: org.name,
    subscriptionTier: org.subscriptionTier,
    myRole: org.myRole,
    memberCount: org.memberCount,
  };
  mixpanel.register(props);
  mixpanel.people.set(props);
}

const ORGANIZATION_PROPS = ['orgId', 'orgName', 'subscriptionTier', 'myRole', 'memberCount'];

export function clearOrganization() {
  if (!enabled) return;
  ORGANIZATION_PROPS.forEach(prop => mixpanel.unregister(prop));
}

export function resetAnalytics() {
  if (!canIdentify()) return;
  mixpanel.reset();
}

// Test-only: lets each test start from an uninitialized state.
export function __resetForTests() {
  enabled = false;
}
